import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import BrokenImageOutlinedIcon from "@material-ui/icons/BrokenImageOutlined";
import DocumentImageViewer from "./DocumentImageViewer";

export type StripPhoto = {
  url: string;
  /** Rótulo del visor a pantalla completa y texto alternativo del thumbnail. */
  label?: string;
};

type PhotoThumbnailStripProps = {
  photos: StripPhoto[];
  /** Lado del thumbnail en px. El formulario usa 80; en modales angostos conviene menos. */
  size?: number;
  /** Qué mostrar cuando no hay ninguna foto. Sin esto el componente no renderiza nada. */
  emptyLabel?: string;
};

const useStyles = makeStyles(() => ({
  thumbnails: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 4,
  },
  thumb: {
    width: ({ size }: { size: number }) => size,
    height: ({ size }: { size: number }) => size,
    borderRadius: 10,
    overflow: "hidden",
    flexShrink: 0,
    cursor: "pointer",
    padding: 0,
    border: "1px solid #e2e8f0",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
    },
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  /** La imagen se sirve con un token que puede no estar listo: mejor un placeholder que un roto. */
  broken: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    background: "#f8fafc",
    color: "#94a3b8",
    cursor: "default",
  },
  empty: {
    color: "#94a3b8",
    fontSize: 14,
  },
}));

/**
 * Tira de miniaturas que se expanden a pantalla completa al hacer click.
 *
 * Es la parte visual de `ObservationsReadOnlyImageStrip`, extraída para poder usarla fuera de un
 * formulario: acá las fotos llegan como URLs ya resueltas en vez de nombres de campo.
 */
const PhotoThumbnailStrip: React.FC<PhotoThumbnailStripProps> = ({
  photos,
  size = 80,
  emptyLabel,
}) => {
  const classes = useStyles({ size });
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [brokenUrls, setBrokenUrls] = useState<string[]>([]);

  if (photos.length === 0) {
    return emptyLabel ? <span className={classes.empty}>{emptyLabel}</span> : null;
  }

  const abrir = (index: number) => setViewerIndex(index);

  return (
    <>
      <div className={classes.thumbnails}>
        {photos.map((photo, index) => {
          const roto = brokenUrls.includes(photo.url);
          const alt = photo.label || `Foto ${index + 1}`;
          return (
            <button
              key={`${photo.url}-${index}`}
              type="button"
              className={classes.thumb}
              onClick={roto ? undefined : () => abrir(index)}
              aria-label={roto ? `${alt} (no se pudo cargar)` : `Ampliar ${alt}`}
              title={roto ? "No se pudo cargar la imagen" : alt}
            >
              {roto ? (
                <span className={classes.broken}>
                  <BrokenImageOutlinedIcon fontSize="small" />
                </span>
              ) : (
                <img
                  src={photo.url}
                  alt={alt}
                  className={classes.img}
                  onError={() => setBrokenUrls((prev) => [...prev, photo.url])}
                />
              )}
            </button>
          );
        })}
      </div>

      {viewerIndex !== null && photos[viewerIndex] && (
        <DocumentImageViewer
          open
          onClose={() => setViewerIndex(null)}
          imageUrl={photos[viewerIndex].url}
          alt={photos[viewerIndex].label || "Foto"}
          fileName={photos[viewerIndex].label || "Foto"}
        />
      )}
    </>
  );
};

export default PhotoThumbnailStrip;
